import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { UserCheck, BarChart2, FileText, KeyRound, FileCheck2, AlertCircle, ArrowRight } from "lucide-react";
interface ChecklistItem {
  question: string;
  answer: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const checklist: ChecklistItem[] = [{
  question: "Who will actually do the work?",
  answer: "I plan and carry out the work myself. There are no account managers relaying messages or junior hand-offs behind the scenes, so you always speak directly with the consultant analysing your website.",
  icon: UserCheck
}, {
  question: "How will success be measured?",
  answer: "Success is agreed up front around qualified organic traffic, enquiries and the commercial goals you set. Progress is reported clearly from Google Search Console and analytics, with no vanity metrics.",
  icon: BarChart2
}, {
  question: "How will priorities be explained?",
  answer: "I provide written recommendations ranked by impact and effort in plain British English, ensuring your developers and content writers know exactly what to address first.",
  icon: FileText
}, {
  question: "What access will you need?",
  answer: "I request only what the agreed work requires, such as Google Search Console, analytics or CMS access. You control these permissions and can revoke them at any time.",
  icon: KeyRound
}, {
  question: "How are scope and terms agreed?",
  answer: "Deliverables and terms are confirmed in writing before work begins. I do not use fixed public packages or generic tiers, shaping the scope around your specific website requirements.",
  icon: FileCheck2
}, {
  question: "What won't you promise?",
  answer: "I make no guaranteed rankings or first-page positions, as search engines control their own results. Instead, I commit to careful analysis, clear priorities, considered execution and honest reporting.",
  icon: AlertCircle
}];
export default function BeforeYouHire() {
  return <section id="before-you-hire" aria-labelledby="before-you-hire-heading" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/40">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-14">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary mb-3">
            Hiring Checklist
          </p>
          <h2 id="before-you-hire-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
            Questions to Ask Before You Hire a Freelance SEO Consultant
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Before engaging an independent consultant, clear answers on delivery, measurement and communication help ensure you choose the right partner for your business.
          </p>
        </div>

        {/* 1/2-column grid of questions & answers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12 sm:mb-16">
          {checklist.map(item => {
          const Icon = item.icon;
          return <Card key={item.question} className="bg-card border-border shadow-xs hover:border-primary/40 transition-colors">
                <CardHeader className="pb-3">
                  <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle>
                    <h3 className="font-serif text-lg sm:text-xl font-bold text-card-foreground leading-snug">
                      {item.question}
                    </h3>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {item.answer}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Action Callout */}
        <div className="p-8 sm:p-10 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-base sm:text-lg text-foreground font-medium leading-relaxed">
              See how I have worked with other businesses in my{" "}
              <Link to="/CaseStudiesPage" className="text-primary underline underline-offset-4 font-medium hover:text-primary/80 transition-colors">
                case studies
              </Link>
              .
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="rounded-md">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}